/**
 * GlobalView — Sidebar Component
 *
 * Left sidebar with 4 tab icons (§11.3):
 * Layers, Feeds, Selection, Events.
 * Collapsible between expanded (280px) and icon-only (56px).
 */

import { useState, useMemo } from 'react';
import styles from './Sidebar.module.css';
import { useEntityStore, selectAircraftCount, selectTrainCount, selectSatelliteCount, selectJammingCellCount, selectZoneCount, selectShipCount, selectEarthquakeCount, selectGlobalEarthquakeCount, selectSwissEarthquakeCount, selectWildfireCount, selectNaturalEventCount, selectNaturalEventCategoryCounts } from '../../stores/entityStore';
import { categoryToIcon, categoryToColor } from '../../services/eonetClient';
import { useGlobeStore } from '../../stores/globeStore';
import { connect as wsConnect, disconnect as wsDisconnect, isConnected } from '../../services/wsClient';

type SidebarTab = 'layers' | 'feeds' | 'selection' | 'events';

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const TABS: { id: SidebarTab; label: string; icon: string }[] = [
  { id: 'layers', label: 'Layers', icon: '◉' },
  { id: 'feeds', label: 'Feeds', icon: '◎' },
  { id: 'selection', label: 'Selection', icon: '◇' },
  { id: 'events', label: 'Events', icon: '⚡' },
];

export default function Sidebar({ collapsed: _collapsed, onToggle: _onToggle }: SidebarProps) {
  const [activeTab, setActiveTab] = useState<SidebarTab>('layers');

  return (
    <aside
      className={styles.sidebar}
      id="app-sidebar"
    >
      {/* HUD bracket corners */}
      <span className={styles.bracketTL} />
      <span className={styles.bracketBL} />
      {/* Tab icons */}
      <nav className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.label}
            id={`sidebar-tab-${tab.id}`}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* Tab content — always visible */}
      <div className={styles.tabContent}>
        <div className={styles.tabContentHeader}>
          <span className={styles.tabContentTitle}>
            {TABS.find((t) => t.id === activeTab)?.label}
          </span>
        </div>

        <div className={styles.tabContentBody}>
          {activeTab === 'layers' && (
            <LayersPanel />
          )}
          {activeTab === 'feeds' && (
            <PlaceholderContent text="Kamera- und Sensorquellen" phase="Phase 3+" />
          )}
          {activeTab === 'selection' && (
            <PlaceholderContent text="Kein Objekt ausgewählt" phase="Phase 2+" />
          )}
          {activeTab === 'events' && (
            <PlaceholderContent text="Keine Events geladen" phase="Phase 5+" />
          )}
        </div>
      </div>
    </aside>
  );
}

function LayersPanel() {
  const aircraftCount = useEntityStore(selectAircraftCount);
  const trainCount = useEntityStore(selectTrainCount);
  const satelliteCount = useEntityStore(selectSatelliteCount);
  const wsConnected = useEntityStore((s) => s.wsConnected);
  const airLayerVisible = useGlobeStore((s) => s.airLayerVisible);
  const airLabelsVisible = useGlobeStore((s) => s.airLabelsVisible);
  const railLayerVisible = useGlobeStore((s) => s.railLayerVisible);
  const railLabelsVisible = useGlobeStore((s) => s.railLabelsVisible);
  const spaceLayerVisible = useGlobeStore((s) => s.spaceLayerVisible);
  const spaceLabelsVisible = useGlobeStore((s) => s.spaceLabelsVisible);
  const starlinkVisible = useGlobeStore((s) => s.starlinkVisible);
  const toggleAirLayer = useGlobeStore((s) => s.toggleAirLayer);
  const toggleAirLabels = useGlobeStore((s) => s.toggleAirLabels);
  const toggleRailLayer = useGlobeStore((s) => s.toggleRailLayer);
  const toggleRailLabels = useGlobeStore((s) => s.toggleRailLabels);
  const toggleSpaceLayer = useGlobeStore((s) => s.toggleSpaceLayer);
  const toggleSpaceLabels = useGlobeStore((s) => s.toggleSpaceLabels);
  const toggleStarlink = useGlobeStore((s) => s.toggleStarlink);
  const ewLayerVisible = useGlobeStore((s) => s.ewLayerVisible);
  const toggleEWLayer = useGlobeStore((s) => s.toggleEWLayer);
  const jammingCellCount = useEntityStore(selectJammingCellCount);
  const zonesLayerVisible = useGlobeStore((s) => s.zonesLayerVisible);
  const toggleZonesLayer = useGlobeStore((s) => s.toggleZonesLayer);
  const zoneCount = useEntityStore(selectZoneCount);
  const seaLayerVisible = useGlobeStore((s) => s.seaLayerVisible);
  const seaLabelsVisible = useGlobeStore((s) => s.seaLabelsVisible);
  const toggleSeaLayer = useGlobeStore((s) => s.toggleSeaLayer);
  const toggleSeaLabels = useGlobeStore((s) => s.toggleSeaLabels);
  const shipCount = useEntityStore(selectShipCount);
  const quakeLayerVisible = useGlobeStore((s) => s.quakeLayerVisible);
  const toggleQuakeLayer = useGlobeStore((s) => s.toggleQuakeLayer);
  const earthquakeCount = useEntityStore(selectEarthquakeCount);
  const quakeGlobalVisible = useGlobeStore((s) => s.quakeGlobalVisible);
  const toggleQuakeGlobal = useGlobeStore((s) => s.toggleQuakeGlobal);
  const globalEarthquakeCount = useEntityStore(selectGlobalEarthquakeCount);
  const quakeSwissVisible = useGlobeStore((s) => s.quakeSwissVisible);
  const toggleQuakeSwiss = useGlobeStore((s) => s.toggleQuakeSwiss);
  const swissEarthquakeCount = useEntityStore(selectSwissEarthquakeCount);
  const fireLayerVisible = useGlobeStore((s) => s.fireLayerVisible);
  const toggleFireLayer = useGlobeStore((s) => s.toggleFireLayer);
  const wildfireCount = useEntityStore(selectWildfireCount);
  const eonetLayerVisible = useGlobeStore((s) => s.eonetLayerVisible);
  const toggleEonetLayer = useGlobeStore((s) => s.toggleEonetLayer);
  const naturalEventCount = useEntityStore(selectNaturalEventCount);
  // Derive category breakdown with useMemo to avoid infinite re-render
  // (selectNaturalEventCategoryCounts returns a new Map each call)
  const eonetCategoryList = useMemo(() => {
    if (!eonetLayerVisible || naturalEventCount === 0) return [];
    const counts = selectNaturalEventCategoryCounts(useEntityStore.getState());
    return Array.from(counts.entries())
      .sort((a, b) => b[1].count - a[1].count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eonetLayerVisible, naturalEventCount]);

  const categories = [
    { name: 'Base', color: 'var(--text-dim)', enabled: true, count: undefined, toggleable: false },
    { name: 'Air', color: 'var(--accent)', enabled: airLayerVisible && wsConnected, count: airLayerVisible ? aircraftCount : undefined, toggleable: true },
    { name: 'Rail', color: '#4a9a2f', enabled: railLayerVisible && wsConnected, count: railLayerVisible ? trainCount : undefined, toggleable: true },
    { name: 'Space', color: '#FFD700', enabled: spaceLayerVisible && wsConnected, count: spaceLayerVisible ? satelliteCount : undefined, toggleable: true },
    { name: 'Maritime', color: 'var(--accent4)', enabled: seaLayerVisible && wsConnected, count: seaLayerVisible ? shipCount : undefined, toggleable: true },
    { name: 'Ground', color: 'var(--accent5)', enabled: false, count: undefined, toggleable: false },
    { name: 'EW / Signal', color: 'var(--accent3)', enabled: ewLayerVisible, count: ewLayerVisible ? jammingCellCount : undefined, toggleable: true },
    { name: 'Zones', color: '#ffaa00', enabled: zonesLayerVisible, count: zonesLayerVisible ? zoneCount : undefined, toggleable: true },
    { name: 'Earthquake', color: '#ff4444', enabled: quakeLayerVisible, count: quakeLayerVisible ? earthquakeCount : undefined, toggleable: true },
    { name: 'Wildfire', color: '#ff6600', enabled: fireLayerVisible, count: fireLayerVisible ? wildfireCount : undefined, toggleable: true },
    { name: 'Natural Events', color: '#9966ff', enabled: eonetLayerVisible, count: eonetLayerVisible ? naturalEventCount : undefined, toggleable: true },
  ];

  /** Check if any live layer will remain active */
  const anyLayerActive = (excludeLayer?: string) => {
    const layers = [
      { name: 'Air', visible: airLayerVisible },
      { name: 'Rail', visible: railLayerVisible },
      { name: 'Space', visible: spaceLayerVisible },
      { name: 'Maritime', visible: seaLayerVisible },
    ];
    return layers.some((l) => l.name !== excludeLayer && l.visible);
  };

  const handleToggle = (cat: { name: string; toggleable: boolean }) => {
    if (!cat.toggleable) return;
    if (cat.name === 'Air') {
      toggleAirLayer();
      const nextVisible = !airLayerVisible;
      if (nextVisible || anyLayerActive('Air')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Rail') {
      toggleRailLayer();
      const nextVisible = !railLayerVisible;
      if (nextVisible || anyLayerActive('Rail')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Space') {
      toggleSpaceLayer();
      const nextVisible = !spaceLayerVisible;
      if (nextVisible || anyLayerActive('Space')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'EW / Signal') {
      toggleEWLayer();
    }
    if (cat.name === 'Maritime') {
      toggleSeaLayer();
      const nextVisible = !seaLayerVisible;
      if (nextVisible || anyLayerActive('Maritime')) {
        if (!isConnected()) wsConnect();
      } else {
        wsDisconnect();
      }
    }
    if (cat.name === 'Zones') {
      toggleZonesLayer();
    }
    if (cat.name === 'Earthquake') {
      toggleQuakeLayer();
    }
    if (cat.name === 'Wildfire') {
      toggleFireLayer();
    }
    if (cat.name === 'Natural Events') {
      toggleEonetLayer();
    }
  };

  return (
    <>
      <div className={styles.layerSectionLabel}>LAYERS</div>
      <div className={styles.layerList}>
        {categories.map((cat) => (
          <div key={cat.name}>
            <div
              className={`${styles.layerItem} ${cat.toggleable ? styles.layerItemClickable : ''}`}
              onClick={() => handleToggle(cat)}
            >
              <span
                className={styles.layerDot}
                style={{ background: cat.color, opacity: cat.enabled ? 1 : 0.3 }}
              />
              <span className={styles.layerName}>{cat.name}</span>
              <span className={styles.layerStatus}>
                {cat.count !== undefined ? `${cat.count}` : cat.enabled ? 'ON' : 'OFF'}
              </span>
            </div>
            {/* Sub-option: Label toggle for Air layer */}
            {cat.name === 'Air' && airLayerVisible && (
              <div
                className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                onClick={toggleAirLabels}
              >
                <span className={styles.layerSubLabel}>Labels</span>
                <span className={styles.layerSubToggle}>
                  {airLabelsVisible ? 'ON' : 'OFF'}
                </span>
              </div>
            )}
            {/* Sub-option: Label toggle for Rail layer */}
            {cat.name === 'Rail' && railLayerVisible && (
              <div
                className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                onClick={toggleRailLabels}
              >
                <span className={styles.layerSubLabel}>Labels</span>
                <span className={styles.layerSubToggle}>
                  {railLabelsVisible ? 'ON' : 'OFF'}
                </span>
              </div>
            )}
            {/* Sub-options for Space layer */}
            {cat.name === 'Space' && spaceLayerVisible && (
              <>
                <div
                  className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                  onClick={toggleSpaceLabels}
                >
                  <span className={styles.layerSubLabel}>Labels</span>
                  <span className={styles.layerSubToggle}>
                    {spaceLabelsVisible ? 'ON' : 'OFF'}
                  </span>
                </div>
                <div
                  className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                  onClick={toggleStarlink}
                >
                  <span className={styles.layerSubLabel}>Starlink</span>
                  <span className={styles.layerSubToggle}>
                    {starlinkVisible ? 'ON' : 'OFF'}
                  </span>
                </div>
              </>
            )}
            {/* Sub-options for Earthquake layer */}
            {cat.name === 'Earthquake' && quakeLayerVisible && (
              <>
                <div
                  className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                  onClick={toggleQuakeGlobal}
                >
                  <span className={styles.layerSubLabel}>Erdbeben Global</span>
                  <span className={styles.layerSubToggle}>
                    {quakeGlobalVisible ? (globalEarthquakeCount > 0 ? `${globalEarthquakeCount}` : 'ON') : 'OFF'}
                  </span>
                </div>
                <div
                  className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                  onClick={toggleQuakeSwiss}
                >
                  <span className={styles.layerSubLabel}>Erdbeben Schweiz</span>
                  <span className={styles.layerSubToggle}>
                    {quakeSwissVisible ? (swissEarthquakeCount > 0 ? `${swissEarthquakeCount}` : 'ON') : 'OFF'}
                  </span>
                </div>
              </>
            )}
            {/* Sub-option: Label toggle for Maritime layer */}
            {cat.name === 'Maritime' && seaLayerVisible && (
              <div
                className={`${styles.layerSubItem} ${styles.layerItemClickable}`}
                onClick={toggleSeaLabels}
              >
                <span className={styles.layerSubLabel}>Labels</span>
                <span className={styles.layerSubToggle}>
                  {seaLabelsVisible ? 'ON' : 'OFF'}
                </span>
              </div>
            )}
            {/* Sub-items: EONET category breakdown */}
            {cat.name === 'Natural Events' && eonetLayerVisible && eonetCategoryList.length > 0 && (
              <>
                {eonetCategoryList.map(([catId, { count: catCount, title: catTitle }]) => (
                    <div
                      key={catId}
                      className={styles.layerSubItem}
                    >
                      <span className={styles.layerSubLabel}>
                        <span style={{ marginRight: 6 }}>{categoryToIcon(catId)}</span>
                        {catTitle}
                      </span>
                      <span
                        className={styles.layerSubToggle}
                        style={{ color: categoryToColor(catId) }}
                      >
                        {catCount}
                      </span>
                    </div>
                  ))}
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function PlaceholderContent({ text, phase }: { text: string; phase: string }) {
  return (
    <div className={styles.placeholder}>
      <span className={styles.placeholderText}>{text}</span>
      <span className={styles.placeholderPhase}>{phase}</span>
    </div>
  );
}
